/* =====================================================================
 * Card — 공통 카드 컴포넌트 (테마 기반)
 * ===================================================================== */

import React from 'react';
import styled, { css } from 'styled-components';

type CardVariant = 'default' | 'outlined' | 'elevated';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  maxWidth?: string;
}

interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {}
interface CardBodyProps extends React.HTMLAttributes<HTMLDivElement> {}
interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  as?: 'h1' | 'h2' | 'h3' | 'h4';
}
interface CardDescProps extends React.HTMLAttributes<HTMLParagraphElement> {}

export const Card: React.FC<CardProps> = ({
  variant = 'default',
  padding = 'md',
  fullWidth = false,
  maxWidth,
  children,
  ...props
}) => (
  <StyledCard $variant={variant} $padding={padding} $fullWidth={fullWidth} $maxWidth={maxWidth} {...props}>
    {children}
  </StyledCard>
);

export const CardHeader: React.FC<CardHeaderProps> = ({ children, ...props }) => (
  <StyledCardHeader {...props}>{children}</StyledCardHeader>
);

export const CardBody: React.FC<CardBodyProps> = ({ children, ...props }) => (
  <StyledCardBody {...props}>{children}</StyledCardBody>
);

export const CardTitle: React.FC<CardTitleProps> = ({ as = 'h2', children, ...props }) => (
  <StyledCardTitle as={as} {...props}>{children}</StyledCardTitle>
);

export const CardDesc: React.FC<CardDescProps> = ({ children, ...props }) => (
  <StyledCardDesc {...props}>{children}</StyledCardDesc>
);

/* ---- Styles ---- */

const paddingMap = {
  none: '0',
  sm: '16px',
  md: '24px',
  lg: '36px',
};

const variantStyles = {
  default: css`
    background: ${({ theme }) => theme.colors.bgPrimary};
    border: 1px solid ${({ theme }) => theme.colors.borderLight};
  `,
  outlined: css`
    background: ${({ theme }) => theme.colors.bgPrimary};
    border: 1px solid ${({ theme }) => theme.colors.border};
  `,
  elevated: css`
    background: ${({ theme }) => theme.colors.bgPrimary};
    border: 1px solid ${({ theme }) => theme.colors.borderLight};
    box-shadow: ${({ theme }) => theme.shadows.md};
  `,
};

const StyledCard = styled.div<{
  $variant: CardVariant;
  $padding: 'none' | 'sm' | 'md' | 'lg';
  $fullWidth: boolean;
  $maxWidth?: string;
}>`
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
  padding: ${({ $padding }) => paddingMap[$padding]};
  display: flex;
  flex-direction: column;
  gap: 14px;

  ${({ $variant }) => variantStyles[$variant]}
  ${({ $fullWidth }) => $fullWidth && css`width: 100%;`}
  ${({ $maxWidth }) => $maxWidth && css`max-width: ${$maxWidth};`}

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ $padding }) =>
      $padding === 'lg' ? '24px 16px' : paddingMap[$padding]};
  }
`;

const StyledCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const StyledCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StyledCardTitle = styled.h2`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  letter-spacing: -0.02em;
`;

const StyledCardDesc = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
  line-height: 1.5;
`;
